import {PreferenceTypes} from 'mattermost-redux/action_types';

import {Client4} from 'mattermost-redux/client';

import {Preferences} from '../constants';

import {getMyPreferences as getMyPreferencesSelector, makeGetCategory} from 'mattermost-redux/selectors/entities/preferences';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/users';

import {type GetStateFunc, type DispatchFunc, type ActionFunc} from 'mattermost-redux/types/actions';
import {type PreferenceType} from 'mattermost-redux/types/preferences';
import {type Theme} from 'mattermost-redux/types/themes';

import {getPreferenceKey} from 'mattermost-redux/utils/preference_utils';

import {fetchUsersByIds} from 'features/users/actions/fetch_users_by_ids';

import {getDMChannelShowPreference} from 'features/sidebar/selectors/get_dm_channel_show_preference';

import {getGMChannelShowPreference} from 'features/sidebar/selectors/get_gm_channel_show_preference';

import {getChannelAndMyMember, getMyChannelMember} from './channels';
import {bindClientFunc} from './helpers';
import {getProfilesInChannel} from './users';

export function deletePreferences(userId: string, preferences: PreferenceType[]): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const myPreferences = getMyPreferencesSelector(state);
        const currentPreferences = preferences.map((pref) => myPreferences[getPreferenceKey(pref.category, pref.name)]);

        (async function deletePreferencesWrapper() {
            try {
                dispatch({
                    type: PreferenceTypes.DELETED_PREFERENCES,
                    data: preferences,
                });

                await Client4.deletePreferences(userId, preferences);
            } catch {
                dispatch({
                    type: PreferenceTypes.RECEIVED_PREFERENCES,
                    data: currentPreferences,
                });
            }
        }());

        return {data: true};
    };
}

export function getMyPreferences(pureError?: boolean): ActionFunc {
    return bindClientFunc({
        clientFunc: Client4.getMyPreferences,
        onSuccess: PreferenceTypes.RECEIVED_ALL_PREFERENCES,
        pureError,
    });
}

export function makeDirectChannelVisibleIfNecessary(otherUserId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);

        const isDMShowed = getDMChannelShowPreference(state, otherUserId, false);

        if (!isDMShowed) {
            dispatch(fetchUsersByIds({
                userIds: [otherUserId],
            }));
            dispatch(savePreferences(currentUserId, [{
                user_id: currentUserId,
                category: Preferences.CATEGORY_DIRECT_CHANNEL_SHOW,
                name: otherUserId,
                value: 'true',
            }]));
        }

        return {data: true};
    };
}

export function makeGroupMessageVisibleIfNecessary(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);
        const {channels} = state.entities.channels;

        const isGMShowed = getGMChannelShowPreference(state, channelId);

        if (!isGMShowed) {
            if (channels[channelId]) {
                dispatch(getMyChannelMember(channelId));
            } else {
                dispatch(getChannelAndMyMember(channelId));
            }

            dispatch(getProfilesInChannel(channelId, 0));
            dispatch(savePreferences(currentUserId, [{
                user_id: currentUserId,
                category: Preferences.CATEGORY_GROUP_CHANNEL_SHOW,
                name: channelId,
                value: 'true',
            }]));
        }

        return {data: true};
    };
}

export function setActionsMenuInitialisationState(initializationState: Record<string, boolean>) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);
        const preference: PreferenceType = {
            user_id: currentUserId,
            category: Preferences.CATEGORY_ACTIONS_MENU,
            name: Preferences.NAME_ACTIONS_MENU_TUTORIAL_STATE,
            value: JSON.stringify(initializationState),
        };
        await dispatch(savePreferences(currentUserId, [preference]));
    };
}

export function setCustomStatusInitialisationState(initializationState: Record<string, boolean>) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);
        const preference: PreferenceType = {
            user_id: currentUserId,
            category: Preferences.CATEGORY_CUSTOM_STATUS,
            name: Preferences.NAME_CUSTOM_STATUS_TUTORIAL_STATE,
            value: JSON.stringify(initializationState),
        };
        await dispatch(savePreferences(currentUserId, [preference]));
    };
}

export function savePreferences(userId: string, preferences: PreferenceType[]) {
    return async (dispatch: DispatchFunc) => {
        try {
            dispatch({
                type: PreferenceTypes.RECEIVED_PREFERENCES,
                data: preferences,
            });

            await Client4.savePreferences(userId, preferences);
            return {data: true};
        } catch {
            dispatch({
                type: PreferenceTypes.DELETED_PREFERENCES,
                data: preferences,
            });
            return {data: false};
        }
    };
}

export function saveTheme(teamId: string, theme: Theme): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);
        const preference: PreferenceType = {
            user_id: currentUserId,
            category: Preferences.CATEGORY_THEME,
            name: teamId || '',
            value: JSON.stringify(theme),
        };

        await savePreferences(currentUserId, [preference])(dispatch);
        return {data: true};
    };
}

export function deleteTeamSpecificThemes(): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();

        const themePreferences: PreferenceType[] = makeGetCategory()(state, Preferences.CATEGORY_THEME);
        const currentUserId = getCurrentUserId(state);

        const toDelete = themePreferences.filter((pref) => pref.name !== '');
        if (toDelete.length > 0) {
            await deletePreferences(currentUserId, toDelete)(dispatch, getState);
        }

        return {data: true};
    };
}
